import {
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  useUpdateMutation,
  useGrippContext,
} from '@gripp/shared-logic';
import { UseMutationOptions } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dimensions, GestureResponderEvent, Platform } from 'react-native';

export type EditableItem = {
  id: string;
  name: string;
};

export type UseEditableItemFilterProps = {
  itemType: 'label' | 'category';
  workspaceId?: string;
  useWorkspaceItemsHook: (workspaceId?: string) => {
    labels?: EditableItem[];
    categories?: EditableItem[];
    isLoading: boolean;
    refetch: () => Promise<any>;
  };
  updateMutation: { modelName: string; query: string };
  deleteMutation: { query: string; options?: UseMutationOptions };
  countQuery: {
    cacheKey: (string | null | undefined)[];
    query: string;
    variables: any;
    options: any;
  };
  refetchItems: () => void;
  setFilter: (newFilter: any) => Promise<void>;
  currentFilter: any;
};

export const useEditableItemFilter = ({
  itemType,
  workspaceId,
  useWorkspaceItemsHook,
  updateMutation,
  deleteMutation,
  countQuery,
  refetchItems,
  setFilter,
  currentFilter,
}: UseEditableItemFilterProps) => {
  const isWeb = Platform.OS === 'web';
  const { logger } = useGrippContext();

  // Fetch items for workspace via shared hook
  const {
    labels,
    categories,
    isLoading: itemsLoading,
    refetch: refetchItemsFromHook,
  } = useWorkspaceItemsHook(workspaceId);
  const allItems = itemType === 'label' ? labels : categories;

  // Edit menu state
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editingItemName, setEditingItemName] = useState<string>('');

  // Anchor for flyout placement
  const [anchorPos, setAnchorPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const itemRefs = useRef<Record<string, any>>({});

  // Delete flow state
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  const { mutateAsync: updateItem } = useUpdateMutation(updateMutation);
  const { mutateAsync: deleteItem } = useCustomMutation(deleteMutation);

  const { data: itemRelatedCount } = useCustomQuery({
    ...countQuery,
    cacheKey: [...countQuery.cacheKey, editingItemId],
    variables: {
      ...countQuery.variables,
      filter: {
        ...countQuery.variables.filter,
        ...(editingItemId && { [`${itemType}Ids`]: [editingItemId] }),
      },
    },
    options: countQuery.options || {},
  });

  const resetFlyoutMenu = useCallback(() => {
    setEditingItemId(null);
    setEditingItemName('');
    setAnchorPos(null);
  }, []);

  const onConfirmModalClose = useCallback(() => {
    setConfirmDeleteVisible(false);
    resetFlyoutMenu();
  }, [resetFlyoutMenu]);

  const openEditMenu = useCallback(
    async (item: EditableItem, event?: GestureResponderEvent) => {
      const menuWidth = 260;
      const menuLeftMargin = 110;
      const menuRightMargin = 5;
      const menuHeightEst = 160;
      const { width: windowWidth, height: windowHeight } =
        Dimensions.get('window');

      if (isWeb) {
        try {
          const ev = (event && (event as any).nativeEvent) || {};
          const anchorX =
            typeof ev.pageX === 'number'
              ? ev.pageX
              : typeof ev.clientX === 'number'
              ? ev.clientX + (window?.scrollX || 0)
              : undefined;
          const anchorY =
            typeof ev.pageY === 'number'
              ? ev.pageY
              : typeof ev.clientY === 'number'
              ? ev.clientY + (window?.scrollY || 0)
              : undefined;

          if (typeof anchorX === 'number' && typeof anchorY === 'number') {
            const left = Math.min(
              Math.max(8, anchorX - menuWidth / 2),
              Math.max(8, windowWidth - menuWidth)
            );
            const top = Math.min(
              Math.max(8, anchorY + 8),
              Math.max(8, windowHeight - menuHeightEst)
            );
            setAnchorPos({
              x: left + menuLeftMargin,
              y: top + menuRightMargin,
            });
          } else {
            setAnchorPos({ x: 0, y: 0 });
          }
        } catch (e) {
          setAnchorPos({ x: 0, y: 0 });
        }
      } else {
        setAnchorPos({ x: 0, y: 0 });
      }

      setEditingItemId(item.id);
      setEditingItemName(item.name || '');
    },
    [isWeb, setAnchorPos, setEditingItemId, setEditingItemName]
  );

  const closeEditMenu = useCallback(() => {
    const prevId = editingItemId;
    const prevName = editingItemName;

    if (prevId && typeof prevName === 'string') {
      const origItem = allItems?.find((i: any) => i.id === prevId);
      const origName = origItem?.name || '';

      if (origName !== prevName) {
        (async () => {
          try {
            await updateItem({ input: { id: prevId, name: prevName } });
            await refetchItemsFromHook();
          } catch (error) {
            logger?.error(error, `Failed to update ${itemType}`);
          }
        })();
      }
    }
    resetFlyoutMenu();
  }, [
    editingItemId,
    editingItemName,
    allItems,
    updateItem,
    refetchItemsFromHook,
    resetFlyoutMenu,
    itemType,
  ]);

  const handleDelete = useCallback(async () => {
    if (editingItemId) {
      setConfirmDeleteVisible(true);
    } else {
      closeEditMenu();
      await refetchItemsFromHook();
    }
  }, [refetchItemsFromHook, closeEditMenu, editingItemId]);

  const onConfirmDelete = useCallback(async () => {
    const itemToDelete = editingItemId;
    if (!itemToDelete) return;

    try {
      await (deleteItem as any)({ input: { id: itemToDelete } });
      await refetchItemsFromHook();

      const currentItemIds = currentFilter?.[`${itemType}Ids`] || [];
      const newItemIds = Array.isArray(currentItemIds)
        ? currentItemIds.filter((id: string) => id !== itemToDelete)
        : [];

      await setFilter({ [`${itemType}Ids`]: newItemIds });
    } catch (e) {
      logger?.error(e, `Failed to delete ${itemType}`);
    } finally {
      onConfirmModalClose();
    }
  }, [
    editingItemId,
    deleteItem,
    refetchItemsFromHook,
    currentFilter,
    itemType,
    setFilter,
    onConfirmModalClose,
  ]);

  // Ensure we fetch as soon as workspaceId resolves
  useEffect(() => {
    if (workspaceId) {
      refetchItemsFromHook();
    }
  }, [workspaceId, refetchItemsFromHook]);

  return {
    items: allItems || [],
    editingItemId,
    editingItemName,
    openEditMenu,
    setEditingItemName,
    handleDelete,
    onConfirmDelete,
    confirmDeleteVisible,
    onConfirmModalClose,
    anchorPos,
    itemRefs,
    itemsLoading,
    closeEditMenu,
    itemCount:
      itemRelatedCount?.issue?.findIssues?.count ||
      itemRelatedCount?.media?.listFiles?.count ||
      0,
  };
};
